import { Levels } from '../../../../../typings/api/skymap/rest/v0/.common';
import { TileCoord } from './ortho-tile-typings';
import { TileQuadtree } from './tile-quad-tree';

/**
 * Helper around Levels[] that the selector uses.
 * - Knows min/max zoom
 * - Can answer tile existence at a given zoom
 * - Can build root mosaic
 * - Can build fallback chain to a root tile
 */
export class LevelIndex {
  private readonly byZoom = new Map<number, Levels>();
  readonly minZoom: number;
  readonly maxZoom: number;

  constructor(readonly levels: Levels[]) {
    if (!levels.length) {
      throw new Error('LevelIndex requires non-empty levels[]');
    }

    for (const lvl of levels) {
      this.byZoom.set(lvl.zoom, lvl);
    }

    const zooms = levels.map((l) => l.zoom).sort((a, b) => a - b);
    this.minZoom = zooms[0];
    this.maxZoom = zooms[zooms.length - 1];
  }

  hasZoomLevel(zoom: number): boolean {
    return this.byZoom.has(zoom);
  }

  hasTile(c: TileCoord): boolean {
    const lvl = this.byZoom.get(c.z);
    if (!lvl) {
      return false;
    }
    return c.y >= lvl.rowMin && c.y <= lvl.rowMax && c.x >= lvl.columnMin && c.x <= lvl.columnMax;
  }

  getRoots(): TileCoord[] {
    const lvl = this.byZoom.get(this.minZoom)!;
    const roots: TileCoord[] = [];
    for (let y = lvl.rowMin; y <= lvl.rowMax; y++) {
      for (let x = lvl.columnMin; x <= lvl.columnMax; x++) {
        roots.push({ z: lvl.zoom, x, y });
      }
    }
    return roots;
  }

  /**
   * Returns ancestors of this coord (closest first), stopping at minZoom, for fallback rendering.
   * Only coords that exist in the level index are included. realizeTile() does not request
   * fallbacks—it only uses them if they already exist—so this does not cause extra loads.
   */
  getFallbackChain(coord: TileCoord, qt: TileQuadtree): TileCoord[] {
    const chain: TileCoord[] = [];
    let cur = qt.parentOf(coord);

    while (cur) {
      if (this.hasTile(cur)) {
        chain.push(cur);
      }
      if (cur.z === this.minZoom) {
        break;
      }
      cur = qt.parentOf(cur);
    }
    return chain;
  }
}
